import { Alert, Badge, Button, Combobox, Dialog, Heading, Label, Menu, Pane, Popover, Position, SelectField, Tab, Table, Tablist, Text, TextInputField, toaster } from 'evergreen-ui';
import moment from 'moment';
import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Link } from 'react-router-dom';
import Api from '../../../util/Api';
import { DesignEditorModal } from '../../common/DesignEditor';
import KRichTextArea from '../../common/FormComponents/KRichTextArea';
import { EVENT_TYPES, EVENT_TYPE_OPTIONS } from '../constants/eventTypes';
import EventConsumablesTab from './EventConsumablesTab';

const CONTENT_URL = process.env.REACT_APP_CONTENT_URL || 'https://content.krit.ro/';
const S3_PATH = 'public-assets/event-images/';

const LOCATIONS = [
  'KRIT Ploiesti',
  'KRIT Brasov'
];

const getImageUrl = (imagePath, { useThumb = false, useOriginal = false } = {}) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('data:')) return imagePath;
  const prefix = useOriginal ? 'original_' : useThumb ? 'thumb_' : 'default_';
  return `${CONTENT_URL}${S3_PATH}${prefix}${imagePath}`;
};

const convertImageToBase64 = async (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('data:')) return imagePath;

  try {
    const imageUrl = getImageUrl(imagePath);
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return null;
  }
};

class EventUnifiedModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // View mode state
      selectedTab: 0,
      showFinalizeConfirm: false,
      isFinalizing: false,
      loading: false,
      fullEvent: null,

      // Edit mode state
      isEditing: false,
      saving: false,

      // Form fields
      title: '',
      description: '',
      rules: '',
      prizes: '',
      start: null,
      end: null,
      hasEndTime: false,
      type: '',
      subtype: '',
      capacity: '',
      location: '',
      price: '',
      imageLogo: null,
      imageCalendarBackground: null,
      imageWideBackground: null,
      platformEventId: '',
      titleManuallyEdited: false,

      // Duplicate mode: fan out one copy per start date/time (duration preserved)
      duplicateStarts: [],
      duplicateDurationMs: 0,

      // Design editor state
      designEditorOpen: false,
      designEditorTarget: null, // 'logo' | 'calendar' | 'wide'
      designEditorInitialJson: null, // loaded from API when opening editor
      pendingDesignJsons: {}, // { logo: json, calendar: json, wide: json } - for unsaved images
    };

    this.fileInputLogo = React.createRef();
    this.fileInputCalendar = React.createRef();
    this.fileInputWide = React.createRef();
  }

  componentDidMount() {
    if (this.props.open && this.props.event) {
      this.loadFullEvent();
    }
  }

  componentDidUpdate(prevProps) {
    // Load full event when modal opens or event changes
    if (this.props.open && !prevProps.open && this.props.event) {
      this.loadFullEvent();
      // Start in edit mode if creating new or duplicating
      if (this.props.isCreating) {
        this.enterEditMode();
      } else if (this.props.isDuplicating) {
        this.loadEventForDuplication(this.props.event);
      }
    } else if (this.props.open && this.props.event && this.props.event.id !== prevProps.event?.id) {
      // Event changed (e.g., after duplication) - reload and enter edit mode
      this.loadFullEvent();
      this.enterEditMode();
    }

    // Handle clicking "Duplicate" button while viewing an event
    if (this.props.open && this.props.isDuplicating && !prevProps.isDuplicating) {
      const event = this.state.fullEvent || this.props.event;
      if (event) {
        this.loadEventForDuplication(event);
      }
    }

    // Handle switching from duplicating to editing the new event
    if (this.props.open && prevProps.isDuplicating && !this.props.isDuplicating && this.props.event) {
      this.loadFullEvent();
      this.enterEditMode();
    }

    // Handle new event creation (no event prop)
    if (this.props.open && !prevProps.open && !this.props.event) {
      this.initNewEvent();
    }

    // Reset state when modal closes
    if (prevProps.open && !this.props.open) {
      this.setState({
        selectedTab: 0,
        showFinalizeConfirm: false,
        isFinalizing: false,
        loading: false,
        fullEvent: null,
        isEditing: false,
        saving: false,
      });
    }
  }

  initNewEvent = () => {
    const startDate = this.props.selectedSlot
      ? moment(this.props.selectedSlot.start).hour(13).minute(0).toDate()
      : moment().hour(13).minute(0).toDate();

    this.setState({
      isEditing: true,
      title: '',
      description: '',
      rules: '',
      prizes: '',
      start: startDate,
      end: startDate,
      hasEndTime: false,
      type: '',
      subtype: '',
      capacity: '',
      location: '',
      price: '',
      imageLogo: null,
      imageCalendarBackground: null,
      imageWideBackground: null,
      platformEventId: '',
      titleManuallyEdited: false,
    });
  };

  loadFullEvent = async () => {
    const eventId = this.props.event?.id;
    if (!eventId) return;

    this.setState({ loading: true });
    try {
      const response = await Api.get(`/events/${eventId}`);
      this.setState({ fullEvent: response.data, loading: false });

      // If duplicating, enter edit mode with converted images
      if (this.props.isDuplicating) {
        this.loadEventForDuplication(response.data);
      }
    } catch (error) {
      console.error('Failed to load full event details:', error);
      toaster.danger('Failed to load event details');
      this.setState({ loading: false });
    }
  };

  loadEventForDuplication = async (event) => {
    console.log('loadEventForDuplication called with event:', event?.id);
    
    let imageLogo = null;
    let imageCalendarBackground = null;
    let imageWideBackground = null;
    
    try {
      [imageLogo, imageCalendarBackground, imageWideBackground] = await Promise.all([
        convertImageToBase64(event.imageLogo),
        convertImageToBase64(event.imageCalendarBackground),
        convertImageToBase64(event.imageWideBackground)
      ]);
      console.log('Images converted:', { 
        hasLogo: !!imageLogo, 
        hasCalBg: !!imageCalendarBackground, 
        hasWideBg: !!imageWideBackground 
      });
    } catch (error) {
      console.error('Error converting images for duplication:', error);
    }

    const startMoment = event.start ? moment(event.start) : null;
    const endMoment = event.end ? moment(event.end) : null;
    const hasEndTime = !!(startMoment && endMoment && !startMoment.isSame(endMoment));
    const durationMs = hasEndTime ? endMoment.diff(startMoment) : 0;
    const seedStart = startMoment ? startMoment.toDate() : moment().hour(13).minute(0).toDate();

    this.setState({
      isEditing: true,
      title: event.title || '',
      description: event.description || '',
      rules: event.rules || '',
      prizes: event.prizes || '',
      start: startMoment ? startMoment.toDate() : null,
      end: endMoment ? endMoment.toDate() : null,
      hasEndTime,
      type: event.type || '',
      subtype: event.subtype || '',
      capacity: event.capacity || '',
      location: event.location || '',
      price: event.price || '',
      imageLogo,
      imageCalendarBackground,
      imageWideBackground,
      // Platform event id is unique per upstream event — never carry it into copies
      platformEventId: '',
      titleManuallyEdited: true,
      duplicateStarts: [seedStart],
      duplicateDurationMs: durationMs,
      pendingDesignJsons: {},
    });
  };

  enterEditMode = () => {
    const event = this.state.fullEvent || this.props.event;
    if (!event) return;

    const startMoment = event.start ? moment(event.start) : null;
    const endMoment = event.end ? moment(event.end) : null;
    const hasEndTime = !!(startMoment && endMoment && !startMoment.isSame(endMoment));

    this.setState({
      isEditing: true,
      title: event.title || '',
      description: event.description || '',
      rules: event.rules || '',
      prizes: event.prizes || '',
      start: startMoment ? startMoment.toDate() : null,
      end: endMoment ? endMoment.toDate() : null,
      hasEndTime,
      type: event.type || '',
      subtype: event.subtype || '',
      capacity: event.capacity || '',
      location: event.location || '',
      price: event.price || '',
      imageLogo: event.imageLogo || null,
      imageCalendarBackground: event.imageCalendarBackground || null,
      imageWideBackground: event.imageWideBackground || null,
      platformEventId: event.platformEventId || '',
      titleManuallyEdited: true,
      pendingDesignJsons: {},
    });
  };

  exitEditMode = () => {
    this.setState({ isEditing: false });
  };

  // Edit mode handlers
  generateTitle = (type, subtype) => {
    const parts = [];
    if (type) parts.push(EVENT_TYPES[type] || type);
    if (subtype) parts.push(subtype);
    return parts.join(' - ');
  };

  handleTypeChange = (e) => {
    const newType = e.target.value;
    this.setState({ type: newType });

    if (!this.state.titleManuallyEdited) {
      const newTitle = this.generateTitle(newType, this.state.subtype);
      this.setState({ title: newTitle });
    }
  };

  handleSubtypeChange = (e) => {
    const newSubtype = e.target.value;
    this.setState({ subtype: newSubtype });

    if (!this.state.titleManuallyEdited) {
      const newTitle = this.generateTitle(this.state.type, newSubtype);
      this.setState({ title: newTitle });
    }
  };

  handleTitleChange = (e) => {
    this.setState({
      title: e.target.value,
      titleManuallyEdited: true
    });
  };

  handleStartDateChange = (date) => {
    this.setState((prevState) => {
      const newState = { start: date };
      if (!prevState.hasEndTime) {
        // No separate end time — keep end in sync with start
        newState.end = date;
      } else if (prevState.end && date && prevState.end < date) {
        newState.end = date;
      }
      return newState;
    });
  };

  handleDuplicateStartChange = (index, date) => {
    this.setState((prev) => {
      const next = [...prev.duplicateStarts];
      next[index] = date;
      return { duplicateStarts: next };
    });
  };

  addDuplicateStart = () => {
    this.setState((prev) => {
      const last = prev.duplicateStarts[prev.duplicateStarts.length - 1];
      // Default the new row to a week after the last — recurring tournaments are the common case
      const next = last ? moment(last).add(1, 'week').toDate() : moment().hour(13).minute(0).toDate();
      return { duplicateStarts: [...prev.duplicateStarts, next] };
    });
  };

  removeDuplicateStart = (index) => {
    this.setState((prev) => ({
      duplicateStarts: prev.duplicateStarts.filter((_, i) => i !== index),
    }));
  };

  handleFileChange = (field) => (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        this.setState({ [field]: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  handleRemoveImage = (field) => {
    this.setState({ [field]: null });
  };

  // ===========================================================================
  // Design Editor helpers
  // ===========================================================================

  /**
   * Map target name to the image state field name
   */
  _getImageFieldForTarget = (target) => {
    if (target === 'logo') return 'imageLogo';
    if (target === 'calendar') return 'imageCalendarBackground';
    return 'imageWideBackground';
  };

  /**
   * Extract the base filename (UUID) from an image value.
   * Image values can be: a filename like "abc123.jpg", a full URL, or a data: URL.
   * Returns null for data: URLs (not yet saved to S3).
   */
  _getFilenameFromImage = (imageValue) => {
    if (!imageValue) return null;
    if (imageValue.startsWith('data:')) return null; // not yet saved
    // If it's a URL, extract just the filename
    if (imageValue.startsWith('http')) {
      const parts = imageValue.split('/');
      const last = parts[parts.length - 1];
      // Strip size prefixes like "default_", "thumb_", "original_"
      return last.replace(/^(default_|thumb_|original_)/, '');
    }
    return imageValue;
  };

  /**
   * Open the design editor for a target. If the image already has a filename
   * (saved to S3), try to load the saved design JSON from the API first.
   */
  openDesignEditor = async (target) => {
    const field = this._getImageFieldForTarget(target);
    const imageValue = this.state[field];

    // Check if we have a pending (unsaved) design JSON for this target
    const pendingJson = this.state.pendingDesignJsons[target];
    if (pendingJson) {
      this.setState({ designEditorOpen: true, designEditorTarget: target, designEditorInitialJson: pendingJson });
      return;
    }

    // Try to load from API if image has a filename
    const filename = this._getFilenameFromImage(imageValue);
    if (filename) {
      try {
        const response = await Api.get(`/image-designs/${encodeURIComponent(filename)}`);
        if (response.data && response.data.designJson) {
          this.setState({ designEditorOpen: true, designEditorTarget: target, designEditorInitialJson: response.data.designJson });
          return;
        }
      } catch (e) {
        // 404 = no saved design, that's fine
      }
    }

    // No saved design — open editor with just the flat image
    this.setState({ designEditorOpen: true, designEditorTarget: target, designEditorInitialJson: null });
  };

  /**
   * After the event is saved and images have S3 filenames, save any pending
   * design JSONs to the ImageDesign API.
   */
  savePendingDesignJsons = async () => {
    const { pendingDesignJsons } = this.state;
    const targets = Object.keys(pendingDesignJsons);
    if (targets.length === 0) return;

    // After save, reload event to get the actual S3 filenames
    const eventId = (this.state.fullEvent || this.props.event)?.id;
    if (!eventId) return;

    try {
      const response = await Api.get(`/events/${eventId}`);
      const savedEvent = response.data;

      const saves = targets.map(async (target) => {
        const field = this._getImageFieldForTarget(target);
        const filename = savedEvent[field];
        if (!filename || filename.startsWith('data:')) return; // shouldn't happen after save

        const json = pendingDesignJsons[target];
        if (!json) return;

        try {
          await Api.put(`/image-designs/${encodeURIComponent(filename)}`, {}, {}, {
            designJson: json,
          });
        } catch (e) {
          console.error(`Failed to save design JSON for ${target}:`, e);
        }
      });

      await Promise.all(saves);
      this.setState({ pendingDesignJsons: {} });
    } catch (e) {
      console.error('Failed to save design JSONs:', e);
    }
  };

  handleSave = async () => {
    const { title, description, rules, prizes, start, end, hasEndTime, type, subtype, capacity, location, price, imageLogo, imageCalendarBackground, imageWideBackground, platformEventId } = this.state;
    const isDuplicating = this.props.isDuplicating;

    if (!title || !type || !location || (!isDuplicating && !start)) {
      alert('Please fill in all required fields');
      return;
    }

    const duplicateStarts = (this.state.duplicateStarts || []).filter(Boolean);
    if (isDuplicating && duplicateStarts.length === 0) {
      alert('Adauga cel putin o data de inceput');
      return;
    }

    const eventData = {
      title,
      description,
      rules,
      prizes,
      start,
      end: hasEndTime ? end : start,
      type,
      subtype,
      capacity: capacity ? parseInt(capacity) : null,
      location,
      price: price ? parseFloat(price) : null,
      imageLogo,
      imageCalendarBackground,
      imageWideBackground,
      platformEventId: platformEventId || null,
    };

    this.setState({ saving: true });

    try {
      const event = this.state.fullEvent || this.props.event;
      if (event && !this.props.isDuplicating) {
        const dataToSave = { ...eventData, id: event.id };
        await this.props.onSave(dataToSave);
        // Save pending design JSONs now that we have filenames
        await this.savePendingDesignJsons();
        // Reload event data and switch to view mode
        await this.loadFullEvent();
        this.setState({ saving: false, isEditing: false });
      } else if (isDuplicating) {
        // One copy per chosen start; preserve the original's duration on each
        const events = duplicateStarts.map((s) => ({
          ...eventData,
          start: s,
          end: this.state.duplicateDurationMs > 0
            ? new Date(s.getTime() + this.state.duplicateDurationMs)
            : s,
        }));
        await this.props.onSave(events);
        this.setState({ saving: false });
        // Parent handles closing + list reload
      } else {
        await this.props.onSave(eventData);
        this.setState({ saving: false });
        this.props.onClose();
      }
    } catch (error) {
      console.error('Save failed:', error);
      this.setState({ saving: false });
    }
  };

  handleDelete = async () => {
    this.setState({ saving: true });
    try {
      await this.props.onDelete();
      this.setState({ saving: false });
    } catch (error) {
      console.error('Delete failed:', error);
      this.setState({ saving: false });
    }
  };

  // View mode handlers
  handleStatusChange = async (newStatus) => {
    const event = this.state.fullEvent || this.props.event;
    if (!event) return;

    try {
      await Api.patch(`/events/${event.id}/status`, {}, {}, { status: newStatus });
      toaster.success(`Event ${newStatus === 'published' ? 'published' : newStatus === 'draft' ? 'unpublished' : 'cancelled'} successfully`);

      this.setState(prevState => ({
        fullEvent: { ...prevState.fullEvent, status: newStatus }
      }));

      if (this.props.onStatusChange) {
        this.props.onStatusChange();
      }
    } catch (error) {
      toaster.danger(`Failed to update event status: ${error.message}`);
    }
  };

  handleFinalize = async () => {
    const event = this.state.fullEvent || this.props.event;
    if (!event) return;

    this.setState({ isFinalizing: true });

    try {
      const response = await Api.post(`/events/${event.id}/finalize`, {}, {}, {});
      toaster.success('Event finalized successfully');

      if (response.data) {
        this.setState({
          fullEvent: response.data,
          showFinalizeConfirm: false,
          isFinalizing: false
        });
      } else {
        await this.loadFullEvent();
        this.setState({
          showFinalizeConfirm: false,
          isFinalizing: false
        });
      }

      if (this.props.onStatusChange) {
        this.props.onStatusChange();
      }
    } catch (error) {
      toaster.danger(`Failed to finalize event: ${error.message}`);
      this.setState({ isFinalizing: false });
    }
  };

  canFinalize = () => {
    const event = this.state.fullEvent || this.props.event;
    if (!event) return false;
    if (event.status === 'finalized') return false;
    const now = moment();
    const eventStart = moment(event.start);
    return eventStart.isBefore(now);
  };

  getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'orange', label: 'Draft' },
      published: { color: 'blue', label: 'Published' },
      open: { color: 'green', label: 'Open for Registration' },
      cancelled: { color: 'neutral', label: 'Cancelled' },
      finalized: { color: 'purple', label: 'Finalized' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  getReservationStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'yellow', label: 'In asteptare' },
      confirmed: { color: 'green', label: 'Confirmat' },
      cancelled: { color: 'red', label: 'Anulat' },
      checked_in: { color: 'blue', label: 'Check-in' },
      no_show: { color: 'neutral', label: 'Absent' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  getPaymentStatusBadge = (status) => {
    const statusConfig = {
      unpaid: { color: 'yellow', label: 'Neplatit' },
      paid: { color: 'green', label: 'Platit' },
      refunded: { color: 'orange', label: 'Rambursat' }
    };
    const config = statusConfig[status] || statusConfig.unpaid;
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // Render methods
  renderEditForm = () => {
    const { title, description, start, end, type, subtype, capacity, location, imageLogo, imageCalendarBackground, imageWideBackground, platformEventId, saving, duplicateStarts, duplicateDurationMs } = this.state;
    const isDuplicating = this.props.isDuplicating;

    return (
      <Pane>
        <TextInputField
          label="Event Title"
          placeholder="Enter event title"
          value={title}
          onChange={this.handleTitleChange}
          required
          disabled={saving}
          marginBottom={16}
        />

        <Pane display="grid" gridTemplateColumns="1fr 1fr" gap={16} marginBottom={16}>
          <SelectField
            label="Event Type"
            value={type}
            onChange={this.handleTypeChange}
            required
            disabled={saving}
          >
            <option value="">Select type...</option>
            {EVENT_TYPE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </SelectField>

          <TextInputField
            label="Subtype"
            placeholder="e.g., Local Tournament, OTS Championship, RCQ"
            value={subtype}
            onChange={this.handleSubtypeChange}
            disabled={saving}
          />
        </Pane>

        <Pane display="grid" gridTemplateColumns="1fr 1fr" gap={16} marginBottom={16}>
          <TextInputField
            label="Capacity"
            type="number"
            placeholder="Max number of participants"
            value={capacity}
            onChange={(e) => this.setState({ capacity: e.target.value })}
            disabled={saving}
          />

          <TextInputField
            label="Price"
            type="number"
            placeholder="Event price"
            value={this.state.price || ''}
            onChange={(e) => this.setState({ price: e.target.value })}
            disabled={saving}
          />
        </Pane>

        <Pane display="grid" gridTemplateColumns="1fr 1fr" gap={16} marginBottom={16}>
          <Pane>
            <Label display="block" marginBottom={4} htmlFor="location-combobox">
              Location *
            </Label>
            <Combobox
              id="location-combobox"
              items={LOCATIONS}
              selectedItem={location}
              onChange={selected => this.setState({ location: selected })}
              placeholder="Select or type location..."
              width="100%"
              disabled={saving}
              autocompleteProps={{ title: 'Location' }}
            />
          </Pane>

          <TextInputField
            label="Platform Event ID"
            placeholder="External platform event identifier"
            value={platformEventId || ''}
            onChange={(e) => this.setState({ platformEventId: e.target.value })}
            disabled={saving}
          />
        </Pane>

        {isDuplicating ? (
          <Pane marginBottom={16}>
            <Label display="block" marginBottom={4}>Date și ore de început</Label>
            <Text size={300} color="muted" display="block" marginBottom={8}>
              Se creează câte o copie pentru fiecare dată.
              {duplicateDurationMs > 0 && ` Durata fiecărei copii: ${Math.round(moment.duration(duplicateDurationMs).asHours() * 10) / 10}h.`}
            </Text>
            {(duplicateStarts || []).map((d, i) => (
              <Pane key={i} display="flex" alignItems="center" gap={8} marginBottom={8}>
                <DatePicker
                  selected={d}
                  onChange={(date) => this.handleDuplicateStartChange(i, date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholderText="Selectează data și ora"
                  disabled={saving}
                />
                {duplicateStarts.length > 1 && (
                  <Button intent="danger" appearance="minimal" onClick={() => this.removeDuplicateStart(i)} disabled={saving}>✕</Button>
                )}
              </Pane>
            ))}
            <Button appearance="minimal" onClick={this.addDuplicateStart} disabled={saving}>➕ Adaugă altă dată</Button>
          </Pane>
        ) : (
          <Pane display="grid" gridTemplateColumns="1fr 1fr" gap={16} marginBottom={16}>
            <Pane>
              <Label display="block" marginBottom={4}>Start Date & Time</Label>
              <DatePicker
                selected={start}
                onChange={this.handleStartDateChange}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="dd/MM/yyyy HH:mm"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholderText="Select start date and time"
                disabled={saving}
              />
            </Pane>

            <Pane>
              <Pane display="flex" alignItems="center" marginBottom={4}>
                <input
                  type="checkbox"
                  id="hasEndTime"
                  checked={this.state.hasEndTime}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    this.setState((prev) => ({
                      hasEndTime: checked,
                      end: checked
                        ? moment(prev.start).add(1, 'hour').toDate()
                        : prev.start,
                    }));
                  }}
                  disabled={saving}
                  style={{ marginRight: 6 }}
                />
                <Label htmlFor="hasEndTime" cursor="pointer">Ora de sfârșit diferită</Label>
              </Pane>
              {this.state.hasEndTime && (
                <DatePicker
                  selected={end}
                  onChange={(date) => this.setState({ end: date })}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholderText="Select end date and time"
                  minDate={start}
                  disabled={saving}
                />
              )}
            </Pane>
          </Pane>
        )}

        <Pane marginBottom={16}>
          <Label display="block" marginBottom={4}>Description</Label>
          <KRichTextArea
            value={description}
            onChange={(content, delta, ops, api) => {
              this.setState({ description: api.getHTML() });
            }}
            readOnly={saving}
          />
        </Pane>

        <Pane marginBottom={16}>
          <Label display="block" marginBottom={4}>Rules</Label>
          <KRichTextArea
            value={this.state.rules}
            onChange={(content, delta, ops, api) => {
              this.setState({ rules: api.getHTML() });
            }}
            readOnly={saving}
          />
        </Pane>

        <Pane marginBottom={16}>
          <Label display="block" marginBottom={4}>Prizes</Label>
          <KRichTextArea
            value={this.state.prizes}
            onChange={(content, delta, ops, api) => {
              this.setState({ prizes: api.getHTML() });
            }}
            readOnly={saving}
          />
        </Pane>

        {/* Image Uploads */}
        <Pane marginBottom={16}>
          <Label display="block" marginBottom={8} fontWeight={600}>Images</Label>

          {/* Upload buttons row */}
          <Pane display="flex" gap={16} marginBottom={12} flexWrap="wrap">
            <Pane>
              <input type="file" ref={this.fileInputLogo} accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={this.handleFileChange('imageLogo')} style={{ display: 'none' }} />
              <Button onClick={() => this.fileInputLogo.current.click()} disabled={saving} marginRight={8}>
                {imageLogo ? 'Change Logo' : 'Upload Logo'}
              </Button>
              <Button appearance="minimal" onClick={() => this.openDesignEditor('logo')} disabled={saving} marginRight={4}>
                🎨 Design
              </Button>
              {imageLogo && <Button intent="danger" appearance="minimal" onClick={() => this.handleRemoveImage('imageLogo')} disabled={saving}>✕</Button>}
            </Pane>
            <Pane>
              <input type="file" ref={this.fileInputCalendar} accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={this.handleFileChange('imageCalendarBackground')} style={{ display: 'none' }} />
              <Button onClick={() => this.fileInputCalendar.current.click()} disabled={saving} marginRight={8}>
                {imageCalendarBackground ? 'Change Calendar BG' : 'Upload Calendar BG'}
              </Button>
              <Button appearance="minimal" onClick={() => this.openDesignEditor('calendar')} disabled={saving} marginRight={4}>
                🎨 Design
              </Button>
              {imageCalendarBackground && <Button intent="danger" appearance="minimal" onClick={() => this.handleRemoveImage('imageCalendarBackground')} disabled={saving}>✕</Button>}
            </Pane>
            <Pane>
              <input type="file" ref={this.fileInputWide} accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={this.handleFileChange('imageWideBackground')} style={{ display: 'none' }} />
              <Button onClick={() => this.fileInputWide.current.click()} disabled={saving} marginRight={8}>
                {imageWideBackground ? 'Change Wide BG' : 'Upload Wide BG'}
              </Button>
              <Button appearance="minimal" onClick={() => this.openDesignEditor('wide')} disabled={saving} marginRight={4}>
                🎨 Design
              </Button>
              {imageWideBackground && <Button intent="danger" appearance="minimal" onClick={() => this.handleRemoveImage('imageWideBackground')} disabled={saving}>✕</Button>}
            </Pane>
          </Pane>

          {/* Composite previews */}
          {(imageCalendarBackground || imageWideBackground) && (
            <Pane display="flex" gap={16} flexWrap="wrap" alignItems="flex-start">
              {/* Calendar card preview */}
              {imageCalendarBackground && (
                <Pane>
                  <Text size={300} color="muted" display="block" marginBottom={4}>Calendar Card</Text>
                  <Pane
                    position="relative"
                    width={200}
                    height={200}
                    borderRadius={6}
                    overflow="hidden"
                    border="1px solid #E4E7EB"
                  >
                    <img
                      src={getImageUrl(imageCalendarBackground)}
                      alt="Calendar background"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    {imageLogo && (
                      <img
                        src={getImageUrl(imageLogo)}
                        alt="Logo overlay"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          maxWidth: '60%',
                          maxHeight: '60%',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))',
                        }}
                      />
                    )}
                  </Pane>
                </Pane>
              )}

              {/* Wide background preview */}
              {imageWideBackground && (
                <Pane flex={1} minWidth={280}>
                  <Text size={300} color="muted" display="block" marginBottom={4}>Wide Background</Text>
                  <Pane
                    position="relative"
                    width="100%"
                    paddingTop="52.5%"
                    borderRadius={6}
                    overflow="hidden"
                    border="1px solid #E4E7EB"
                  >
                    <img
                      src={getImageUrl(imageWideBackground)}
                      alt="Wide background"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    {imageLogo && (
                      <img
                        src={getImageUrl(imageLogo)}
                        alt="Logo overlay"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          maxWidth: '40%',
                          maxHeight: '50%',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))',
                        }}
                      />
                    )}
                  </Pane>
                </Pane>
              )}
            </Pane>
          )}

          {/* Logo-only preview when no backgrounds are set */}
          {imageLogo && !imageCalendarBackground && !imageWideBackground && (
            <Pane>
              <Text size={300} color="muted" display="block" marginBottom={4}>Logo</Text>
              <img src={getImageUrl(imageLogo)} alt="Logo preview" style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 4, border: '1px solid #E4E7EB', background: '#F9FAFC' }} />
            </Pane>
          )}

          {/* Design Editor Modal */}
          <DesignEditorModal
            open={this.state.designEditorOpen}
            title={
              this.state.designEditorTarget === 'logo' ? 'Design Logo'
              : this.state.designEditorTarget === 'calendar' ? 'Design Calendar Card'
              : 'Design Wide Background'
            }
            preset={
              this.state.designEditorTarget === 'logo' ? { w: 400, h: 400 }
              : this.state.designEditorTarget === 'calendar' ? { w: 400, h: 400 }
              : { w: 1200, h: 630 }
            }
            initialImage={
              this.state.designEditorTarget === 'logo'
                ? (imageLogo ? getImageUrl(imageLogo, { useOriginal: true }) : null)
                : this.state.designEditorTarget === 'calendar'
                  ? (imageCalendarBackground ? getImageUrl(imageCalendarBackground, { useOriginal: true }) : null)
                  : (imageWideBackground ? getImageUrl(imageWideBackground, { useOriginal: true }) : null)
            }
            designJson={this.state.designEditorInitialJson}
            eventImages={[
              { name: 'Logo', src: imageLogo ? getImageUrl(imageLogo, { useOriginal: true }) : null },
              { name: 'Calendar BG', src: imageCalendarBackground ? getImageUrl(imageCalendarBackground, { useOriginal: true }) : null },
              { name: 'Wide BG', src: imageWideBackground ? getImageUrl(imageWideBackground, { useOriginal: true }) : null },
            ].filter(img => img.src)}
            onClose={() => this.setState({ designEditorOpen: false, designEditorTarget: null, designEditorInitialJson: null })}
            onExport={(dataUrl, designJson) => {
              const target = this.state.designEditorTarget;
              const pendingDesignJsons = { ...this.state.pendingDesignJsons, [target]: designJson };
              if (target === 'logo') {
                this.setState({ imageLogo: dataUrl, pendingDesignJsons });
              } else if (target === 'calendar') {
                this.setState({ imageCalendarBackground: dataUrl, pendingDesignJsons });
              } else {
                this.setState({ imageWideBackground: dataUrl, pendingDesignJsons });
              }
            }}
          />
        </Pane>
      </Pane>
    );
  };

  renderDetailsTab = () => {
    const event = this.state.fullEvent || this.props.event;

    return (
      <Pane>
        {event.status === 'finalized' && (
          <Alert intent="success" marginBottom={16} title="Event Finalized">
            This event has been finalized.
            {event.stockOperationId && (
              <Pane marginTop={8}>
                <Link to={`/stock-operations/${event.stockOperationId}`}>
                  <Button appearance="primary" size="small">
                    View Stock Operation
                  </Button>
                </Link>
              </Pane>
            )}
          </Alert>
        )}

        <Pane marginBottom={24}>
          <Heading size={600} marginBottom={8}>
            {event.title} {this.getStatusBadge(event.status || 'draft')}
          </Heading>
          <Pane display="flex" gap={8} alignItems="center">
            <Badge color="blue">{event.type}</Badge>
            {event.subtype && <Badge color="neutral">{event.subtype}</Badge>}
            <Badge color="green">{event.location}</Badge>
          </Pane>
        </Pane>

        <Pane marginBottom={16}>
          <Text fontWeight={600} display="block" marginBottom={4}>Date & Time</Text>
          <Text>
            {moment(event.start).format('dddd, MMMM D, YYYY')} at {moment(event.start).format('HH:mm')}
            {event.end && !moment(event.start).isSame(moment(event.end)) && (
              <> - {moment(event.end).format('HH:mm')}</>
            )}
          </Text>
        </Pane>

        <Pane display="grid" gridTemplateColumns="1fr 1fr" gap={16} marginBottom={16}>
          {event.capacity && (
            <Pane>
              <Text fontWeight={600} display="block" marginBottom={4}>Capacity</Text>
              <Text>{event.capacity} participants</Text>
            </Pane>
          )}
          {event.price && (
            <Pane>
              <Text fontWeight={600} display="block" marginBottom={4}>Price</Text>
              <Text>{event.price} RON</Text>
            </Pane>
          )}
        </Pane>

        {event.platformEventId && (
          <Pane marginBottom={16}>
            <Text fontWeight={600} display="block" marginBottom={4}>Platform Event ID</Text>
            <Text>{event.platformEventId}</Text>
          </Pane>
        )}

        {event.description && (
          <Pane marginBottom={16}>
            <Text fontWeight={600} display="block" marginBottom={8}>Description</Text>
            <Pane
              dangerouslySetInnerHTML={{ __html: event.description }}
              className="event-description"
              style={{ border: '1px solid #E4E7EB', borderRadius: '3px', padding: '12px', backgroundColor: '#F9FAFC' }}
            />
          </Pane>
        )}

        {event.rules && (
          <Pane marginBottom={16}>
            <Text fontWeight={600} display="block" marginBottom={8}>Rules</Text>
            <Pane
              dangerouslySetInnerHTML={{ __html: event.rules }}
              className="event-rules"
              style={{ border: '1px solid #E4E7EB', borderRadius: '3px', padding: '12px', backgroundColor: '#F9FAFC' }}
            />
          </Pane>
        )}

        {event.prizes && (
          <Pane marginBottom={16}>
            <Text fontWeight={600} display="block" marginBottom={8}>Prizes</Text>
            <Pane
              dangerouslySetInnerHTML={{ __html: event.prizes }}
              className="event-prizes"
              style={{ border: '1px solid #E4E7EB', borderRadius: '3px', padding: '12px', backgroundColor: '#F9FAFC' }}
            />
          </Pane>
        )}

        {(event.imageLogo || event.imageCalendarBackground || event.imageWideBackground) && (
          <Pane marginBottom={16}>
            <Text fontWeight={600} display="block" marginBottom={8}>Images</Text>
            <Pane display="flex" gap={12} flexWrap="wrap" alignItems="flex-start">
              {/* Calendar card composite */}
              {event.imageCalendarBackground && (
                <Pane>
                  <Text size={300} color="muted" display="block" marginBottom={4}>Calendar Card</Text>
                  <Pane position="relative" width={160} height={160} borderRadius={4} overflow="hidden" border="1px solid #E4E7EB">
                    <img src={getImageUrl(event.imageCalendarBackground)} alt="Calendar background" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    {event.imageLogo && (
                      <img src={getImageUrl(event.imageLogo)} alt="Logo" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: '60%', maxHeight: '60%', objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }} />
                    )}
                  </Pane>
                </Pane>
              )}
              {/* Wide background composite */}
              {event.imageWideBackground && (
                <Pane flex={1} minWidth={240}>
                  <Text size={300} color="muted" display="block" marginBottom={4}>Wide Background</Text>
                  <Pane position="relative" width="100%" paddingTop="52.5%" borderRadius={4} overflow="hidden" border="1px solid #E4E7EB">
                    <img src={getImageUrl(event.imageWideBackground)} alt="Wide background" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    {event.imageLogo && (
                      <img src={getImageUrl(event.imageLogo)} alt="Logo" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: '40%', maxHeight: '50%', objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }} />
                    )}
                  </Pane>
                </Pane>
              )}
              {/* Logo-only fallback */}
              {event.imageLogo && !event.imageCalendarBackground && !event.imageWideBackground && (
                <Pane>
                  <Text size={300} color="muted" display="block" marginBottom={4}>Logo</Text>
                  <img src={getImageUrl(event.imageLogo)} alt="Logo" style={{ width: 100, height: 100, objectFit: 'contain', borderRadius: 3, border: '1px solid #E4E7EB', background: '#F9FAFC' }} />
                </Pane>
              )}
            </Pane>
          </Pane>
        )}

        <Pane marginBottom={16}>
          <Text fontWeight={600} display="block" marginBottom={8}>Social Media Preview</Text>
          <Pane display="flex" alignItems="center" gap={12}>
            <Button
              iconBefore="download"
              onClick={() => {
                const previewUrl = `${CONTENT_URL}${S3_PATH}preview_${event.id}_r${event.revision || 1}.jpg`;
                window.open(previewUrl, '_blank');
              }}
            >
              Download Preview Image
            </Button>
            <Text size={300} color="muted">
              1200×630 preview image (generated automatically)
            </Text>
          </Pane>
        </Pane>
      </Pane>
    );
  };

  renderReservationsTab = () => {
    const event = this.state.fullEvent || this.props.event;
    const reservations = event.reservations || [];
    const activeReservations = reservations.filter(r => r.status !== 'cancelled');

    return (
      <Pane>
        <Pane marginBottom={16}>
          <Text fontWeight={600}>
            Rezervari ({activeReservations.length}{event.capacity ? ` / ${event.capacity}` : ''})
          </Text>
        </Pane>

        {reservations.length === 0 ? (
          <Pane padding={32} textAlign="center" backgroundColor="#F9FAFC" borderRadius={3}>
            <Text color="muted">Nu exista rezervari pentru acest eveniment</Text>
          </Pane>
        ) : (
          <Table>
            <Table.Head>
              <Table.TextHeaderCell>Cod</Table.TextHeaderCell>
              <Table.TextHeaderCell>Nume</Table.TextHeaderCell>
              <Table.TextHeaderCell>Email</Table.TextHeaderCell>
              <Table.TextHeaderCell>Telefon</Table.TextHeaderCell>
              <Table.TextHeaderCell>Status</Table.TextHeaderCell>
              <Table.TextHeaderCell>Plata</Table.TextHeaderCell>
              <Table.TextHeaderCell>Actiuni</Table.TextHeaderCell>
            </Table.Head>
            <Table.Body>
              {reservations.map((reservation) => (
                <Table.Row key={reservation.id}>
                  <Table.TextCell>{reservation.confirmationCode}</Table.TextCell>
                  <Table.TextCell>{reservation.name}</Table.TextCell>
                  <Table.TextCell>{reservation.email}</Table.TextCell>
                  <Table.TextCell>{reservation.phone}</Table.TextCell>
                  <Table.TextCell>{this.getReservationStatusBadge(reservation.status)}</Table.TextCell>
                  <Table.TextCell>{this.getPaymentStatusBadge(reservation.paymentStatus)}</Table.TextCell>
                  <Table.TextCell>
                    <Link to={`/event-reservations/${reservation.id}`}>
                      <Button size="small" appearance="minimal">Detalii</Button>
                    </Link>
                  </Table.TextCell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Pane>
    );
  };

  renderEditFooter = () => {
    const { saving } = this.state;
    const event = this.state.fullEvent || this.props.event;
    const isNewEvent = !event;
    const { onDelete, isDuplicating } = this.props;

    return (
      <Pane display="flex" justifyContent="space-between" paddingTop={16} marginTop={16} borderTop="default">
        <Pane>
          {event && onDelete && !isDuplicating && (
            <Button intent="danger" onClick={this.handleDelete} isLoading={saving} disabled={saving}>
              Delete Event
            </Button>
          )}
        </Pane>
        <Pane display="flex" gap={8}>
          {!isNewEvent && !isDuplicating ? (
            <Button onClick={this.exitEditMode} disabled={saving}>
              Cancel
            </Button>
          ) : (
            <Button onClick={this.props.onClose} disabled={saving}>
              Cancel
            </Button>
          )}
          <Button appearance="primary" onClick={this.handleSave} isLoading={saving} disabled={saving}>
            {isDuplicating ? 'Duplicate Event' : (event ? 'Update Event' : 'Create Event')}
          </Button>
        </Pane>
      </Pane>
    );
  };

  renderViewFooter = () => {
    const event = this.state.fullEvent || this.props.event;
    const { onDelete, onDuplicate } = this.props;

    return (
      <Pane display="flex" justifyContent="space-between" paddingTop={16} marginTop={16} borderTop="default">
        <Pane display="flex" gap={8}>
          {onDelete && event.status !== 'finalized' && (
            <Button intent="danger" onClick={onDelete}>Delete</Button>
          )}

          {event.status !== 'finalized' && (
            <Popover
              position={Position.TOP_LEFT}
              content={
                <Menu>
                  <Menu.Group title="Change Status">
                    {event.status === 'draft' && (
                      <Menu.Item onSelect={() => this.handleStatusChange('published')}>Publish Event</Menu.Item>
                    )}
                    {event.status === 'published' && (
                      <>
                        <Menu.Item onSelect={() => this.handleStatusChange('open')}>Open Registration</Menu.Item>
                        <Menu.Item onSelect={() => this.handleStatusChange('draft')}>Unpublish (Back to Draft)</Menu.Item>
                      </>
                    )}
                    {event.status === 'open' && (
                      <Menu.Item onSelect={() => this.handleStatusChange('published')}>Close Registration</Menu.Item>
                    )}
                    {event.status === 'cancelled' && (
                      <Menu.Item onSelect={() => this.handleStatusChange('draft')}>Restore to Draft</Menu.Item>
                    )}
                  </Menu.Group>
                  {(event.status === 'published' || event.status === 'open') && (
                    <Menu.Group>
                      <Menu.Item intent="danger" onSelect={() => this.handleStatusChange('cancelled')}>Cancel Event</Menu.Item>
                    </Menu.Group>
                  )}
                </Menu>
              }
            >
              <Button>Status: {this.getStatusBadge(event.status || 'draft')} ▾</Button>
            </Popover>
          )}
        </Pane>

        <Pane display="flex" gap={8}>
          {this.canFinalize() && (
            <Button
              intent="warning"
              onClick={() => this.setState({ showFinalizeConfirm: true })}
              isLoading={this.state.isFinalizing}
              disabled={this.state.isFinalizing}
            >
              Finalize Event
            </Button>
          )}

          {onDuplicate && (
            <Button onClick={onDuplicate}>Duplicate</Button>
          )}

          {event.status !== 'finalized' && (
            <Button appearance="primary" onClick={this.enterEditMode}>Edit</Button>
          )}

          <Button onClick={this.props.onClose}>Close</Button>
        </Pane>
      </Pane>
    );
  };

  render() {
    const { open, onClose, isDuplicating } = this.props;
    const { selectedTab, loading, isEditing, saving } = this.state;
    const event = this.state.fullEvent || this.props.event;
    const isNewEvent = !event;
    const tabs = ['Detalii Eveniment', 'Rezervari', 'Consumabile'];

    // Determine modal title
    let modalTitle = 'Add New Event';
    if (isDuplicating) {
      modalTitle = 'Duplicate Event';
    } else if (event) {
      modalTitle = isEditing ? `Edit: ${event.title}` : event.title;
    }

    return (
      <Dialog
        isShown={open}
        title={modalTitle}
        onCloseComplete={onClose}
        hasFooter={false}
        width={900}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEscapePress={!saving}
        preventBodyScrolling
        contentContainerProps={{ padding: 0 }}
      >
        <Pane padding={16}>
          {loading && (
            <Pane padding={16} textAlign="center">
              <Text>Loading event details...</Text>
            </Pane>
          )}

          {isEditing || isNewEvent ? (
            // Edit mode
            <>
              {this.renderEditForm()}
              {this.renderEditFooter()}
            </>
          ) : (
            // View mode with tabs
            <>
              <Pane>
                <Tablist marginBottom={16}>
                  {tabs.map((tab, index) => (
                    <Tab
                      key={tab}
                      isSelected={selectedTab === index}
                      onSelect={() => this.setState({ selectedTab: index })}
                    >
                      {tab}
                      {index === 1 && event?.reservations && (
                        <Badge marginLeft={8} color={event.reservations.filter(r => r.status !== 'cancelled').length > 0 ? 'green' : 'neutral'}>
                          {event.reservations.filter(r => r.status !== 'cancelled').length}
                        </Badge>
                      )}
                      {index === 2 && event?.consumables && (
                        <Badge marginLeft={8} color={event.consumables.length > 0 ? 'blue' : 'neutral'}>
                          {event.consumables.length}
                        </Badge>
                      )}
                    </Tab>
                  ))}
                </Tablist>
              </Pane>

              <Pane maxHeight={400} overflowY="auto" paddingRight={8}>
                {selectedTab === 0 && this.renderDetailsTab()}
                {selectedTab === 1 && this.renderReservationsTab()}
                {selectedTab === 2 && event && (
                  <EventConsumablesTab
                    eventId={event.id}
                    consumables={event.consumables || []}
                    isDuplicating={false}
                    disabled={event.status === 'finalized'}
                    isFinalized={event.status === 'finalized'}
                  />
                )}
              </Pane>

              {this.renderViewFooter()}
            </>
          )}
        </Pane>

        {/* Finalize Confirmation Dialog */}
        <Dialog
          isShown={this.state.showFinalizeConfirm}
          title="Finalize Event?"
          intent="warning"
          onCloseComplete={() => this.setState({ showFinalizeConfirm: false })}
          confirmLabel="Yes, Finalize"
          onConfirm={this.handleFinalize}
          isConfirmLoading={this.state.isFinalizing}
        >
          <Pane>
            {(event?.consumables || []).length === 0 ? (
              <>
                <Text>This event has no consumables registered. Finalizing it will not create a stock operation.</Text>
                <Text display="block" marginTop={8}>Are you sure you want to finalize this event?</Text>
              </>
            ) : (
              <>
                <Text display="block">This will create a stock operation to remove <strong>{event?.consumables?.length}</strong> consumable(s) from inventory.</Text>
                <Text display="block" marginTop={8}>This action cannot be undone. Are you sure you want to finalize?</Text>
              </>
            )}
          </Pane>
        </Dialog>
      </Dialog>
    );
  }
}

export default EventUnifiedModal;
